import type { RefObject } from 'react';
import { gsap, useGSAP } from '../lib/gsap';

interface PopInOptions {
  /** 初始 Y 位移(px)，正值=从下方升入 */
  y?: number;
  /** 初始 X 位移(px) */
  x?: number;
  /** 初始缩放 */
  scale?: number;
  /** 时长(s) */
  duration?: number;
  /** transform-origin，决定缩放锚点(下拉一般贴触发点那一角) */
  origin?: string;
}

/**
 * 下拉 / 菜单 / 浮层「弹入」微交互：open 变 true 时对 ref 元素做
 * 缩放 + 淡入 + 轻微位移；reduce-motion 下不做动画(元素恒为自然态)。
 *
 * 用法前提：元素须为「open && <div ref={ref}>」式条件渲染——open 为真时 ref 才挂载。
 * useGSAP 跑在 useLayoutEffect(绘制前)，故 fromTo 先置初态再补间，无闪烁。
 * 动画结束 clearProps 抹掉内联样式，避免残留 transform 影响后续 hover/布局。
 */
export function usePopIn(
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  { y = 6, x = 0, scale = 0.97, duration = 0.18, origin = 'top center' }: PopInOptions = {},
) {
  useGSAP(
    () => {
      const el = ref.current;
      if (!open || !el) return;
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        gsap.fromTo(
          el,
          { autoAlpha: 0, y, x, scale, transformOrigin: origin },
          {
            autoAlpha: 1,
            y: 0,
            x: 0,
            scale: 1,
            duration,
            ease: 'power2.out',
            clearProps: 'transform,opacity,visibility',
          },
        );
      });
      return () => mm.revert();
    },
    { dependencies: [open] },
  );
}
